import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
    Lock,
    KeyRound,
    CheckCircle2,
    AlertCircle,
    Eye,
    EyeOff,
    RefreshCw,
    ShieldCheck
} from 'lucide-react';
import AdminSidebar from '../../components/AdminSidebar';

const AdminProfile: React.FC = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [passwordSaving, setPasswordSaving] = useState(false);

    // Password state
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showCurrent, setShowCurrent] = useState(false);
    const [showNew, setShowNew] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [passwordMsg, setPasswordMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

    useEffect(() => {
        const token = localStorage.getItem('adminToken');
        if (!token) {
            navigate('/admin/login');
        }
    }, [navigate]);

    const handleResetPassword = async (e: React.FormEvent) => {
        e.preventDefault();
        setPasswordMsg(null);

        if (newPassword !== confirmPassword) {
            setPasswordMsg({ type: 'error', text: 'New password and confirm password do not match.' });
            return;
        }

        if (newPassword.length < 6) {
            setPasswordMsg({ type: 'error', text: 'New password must be at least 6 characters.' });
            return;
        }

        setPasswordSaving(true);
        const token = localStorage.getItem('adminToken');
        if (!token) return;

        try {
            const res = await fetch('/api/admin/change-password', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    currentPassword,
                    newPassword
                })
            });

            const data = await res.json();
            if (res.ok) {
                setPasswordMsg({ type: 'success', text: 'Password reset successfully!' });
                setCurrentPassword('');
                setNewPassword('');
                setConfirmPassword('');
            } else {
                setPasswordMsg({ type: 'error', text: data.message || 'Failed to reset password' });
            }
        } catch (err) {
            setPasswordMsg({ type: 'error', text: 'Server connection error' });
        } finally {
            setPasswordSaving(false);
        }
    };

    // Calculate password strength
    const getPasswordStrength = (pass: string) => {
        if (!pass) return { score: 0, label: 'None', color: 'bg-slate-200' };
        let score = 0;
        if (pass.length >= 6) score++;
        if (pass.length >= 10) score++;
        if (/[A-Z]/.test(pass)) score++;
        if (/[0-9]/.test(pass)) score++;
        if (/[^A-Za-z0-9]/.test(pass)) score++;

        if (score <= 2) return { score: 33, label: 'Weak', color: 'bg-red-500' };
        if (score <= 4) return { score: 66, label: 'Good', color: 'bg-amber-500' };
        return { score: 100, label: 'Strong', color: 'bg-emerald-500' };
    };

    const strength = getPasswordStrength(newPassword);

    return (
        <div className="flex h-screen bg-slate-950 font-sans overflow-hidden">
            <AdminSidebar />

            <div className="flex-1 flex flex-col min-w-0 overflow-y-auto bg-slate-900 text-white">
                {/* Top Header */}
                <header className="px-8 py-6 border-b border-slate-800 flex items-center justify-between bg-slate-900/80 backdrop-blur-md sticky top-0 z-20">
                    <div>
                        <h1 className="text-2xl font-black tracking-tight text-white flex items-center gap-3">
                            <KeyRound className="w-7 h-7 text-blue-500" />
                            Reset Admin Password
                        </h1>
                        <p className="text-xs font-medium text-slate-400 mt-1">Update your password to keep your administrator account secure</p>
                    </div>
                </header>

                <main className="p-8 max-w-3xl w-full mx-auto space-y-8 animate-in fade-in duration-300">
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="bg-slate-800/40 border border-slate-800 p-8 rounded-3xl backdrop-blur-xl shadow-2xl space-y-6"
                    >
                        <div className="flex items-center gap-4 pb-6 border-b border-slate-800">
                            <div className="w-14 h-14 rounded-2xl bg-blue-600/20 text-blue-400 flex items-center justify-center font-black shadow-lg">
                                <ShieldCheck className="w-7 h-7" />
                            </div>
                            <div>
                                <h2 className="text-xl font-bold text-white">Administrator Security</h2>
                                <p className="text-xs text-slate-400">Manage your system credentials</p>
                            </div>
                        </div>

                        {passwordMsg && (
                            <div className={`p-4 rounded-2xl text-sm font-medium flex items-center gap-3 ${
                                passwordMsg.type === 'success'
                                    ? 'bg-emerald-500/10 border border-emerald-500/20 text-emerald-400'
                                    : 'bg-red-500/10 border border-red-500/20 text-red-400'
                            }`}>
                                {passwordMsg.type === 'success' ? <CheckCircle2 className="w-5 h-5 shrink-0" /> : <AlertCircle className="w-5 h-5 shrink-0" />}
                                {passwordMsg.text}
                            </div>
                        )}

                        <form onSubmit={handleResetPassword} className="space-y-6">
                            <div className="space-y-2">
                                <label className="text-xs font-bold text-slate-400 uppercase tracking-widest">Current Password</label>
                                <div className="relative">
                                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 w-5 h-5" />
                                    <input
                                        type={showCurrent ? 'text' : 'password'}
                                        value={currentPassword}
                                        onChange={(e) => setCurrentPassword(e.target.value)}
                                        required
                                        className="w-full bg-slate-900/80 border border-slate-700/80 rounded-xl py-3.5 pl-12 pr-12 text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 transition-all font-medium text-sm"
                                        placeholder="Enter current password"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowCurrent(!showCurrent)}
                                        className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white"
                                    >
                                        {showCurrent ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                    </button>
                                </div>
                            </div>

                            <div className="space-y-2">
                                <label className="text-xs font-bold text-slate-400 uppercase tracking-widest">New Password</label>
                                <div className="relative">
                                    <KeyRound className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 w-5 h-5" />
                                    <input
                                        type={showNew ? 'text' : 'password'}
                                        value={newPassword}
                                        onChange={(e) => setNewPassword(e.target.value)}
                                        required
                                        className="w-full bg-slate-900/80 border border-slate-700/80 rounded-xl py-3.5 pl-12 pr-12 text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 transition-all font-medium text-sm"
                                        placeholder="Enter new password (min. 6 characters)"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowNew(!showNew)}
                                        className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white"
                                    >
                                        {showNew ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                    </button>
                                </div>

                                {newPassword && (
                                    <div className="mt-2 space-y-1">
                                        <div className="flex justify-between items-center text-xs">
                                            <span className="text-slate-400">Password Strength:</span>
                                            <span className={`font-bold ${
                                                strength.label === 'Weak' ? 'text-red-400' :
                                                strength.label === 'Good' ? 'text-amber-400' : 'text-emerald-400'
                                            }`}>{strength.label}</span>
                                        </div>
                                        <div className="w-full h-1.5 bg-slate-800 rounded-full overflow-hidden">
                                            <div className={`h-full ${strength.color} transition-all duration-300`} style={{ width: `${strength.score}%` }} />
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="space-y-2">
                                <label className="text-xs font-bold text-slate-400 uppercase tracking-widest">Confirm New Password</label>
                                <div className="relative">
                                    <KeyRound className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 w-5 h-5" />
                                    <input
                                        type={showConfirm ? 'text' : 'password'}
                                        value={confirmPassword}
                                        onChange={(e) => setConfirmPassword(e.target.value)}
                                        required
                                        className="w-full bg-slate-900/80 border border-slate-700/80 rounded-xl py-3.5 pl-12 pr-12 text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 transition-all font-medium text-sm"
                                        placeholder="Confirm new password"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowConfirm(!showConfirm)}
                                        className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white"
                                    >
                                        {showConfirm ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                    </button>
                                </div>
                            </div>

                            <div className="pt-4">
                                <button
                                    type="submit"
                                    disabled={passwordSaving}
                                    className="w-full bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white font-bold py-4 rounded-xl shadow-lg shadow-blue-600/30 flex items-center justify-center gap-2 transition-all"
                                >
                                    {passwordSaving ? <RefreshCw className="w-5 h-5 animate-spin" /> : <KeyRound className="w-5 h-5" />}
                                    <span>{passwordSaving ? 'Resetting Password...' : 'Reset Password'}</span>
                                </button>
                            </div>
                        </form>
                    </motion.div>
                </main>
            </div>
        </div>
    );
};

export default AdminProfile;
